import { useMemo, useState } from 'react';
import {
  FileSpreadsheet, FileDown, Loader2, ImageOff, AlertTriangle,
  Image as ImageIcon, Rows3, ZoomIn
} from 'lucide-react';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';
import { VisualizadorFoto, FotoAmpliavel } from './VisualizadorFoto';
import {
  TabelaFinal, LinhaTabelaFinal, ROTULO_STATUS_PARTE, corClassificacao, moeda,
} from './cotacao-types';

interface Props {
  tabela: TabelaFinal;
  onExportar: (formato: 'xlsx' | 'pdf') => Promise<void>;
}

/**
 * A entrega da cotação: uma linha por parte a corrigir, agrupada por máquina,
 * com o valor da correção e o total por máquina — exatamente o que sai no XLSX
 * e no PDF, para não haver divergência entre o que se vê e o que se manda.
 */
export function TabelaFinalView({ tabela, onExportar }: Props) {
  const [exportando, setExportando] = useState<'xlsx' | 'pdf' | null>(null);
  const [mostrarFotos, setMostrarFotos] = useState(true);
  const [ampliada, setAmpliada] = useState<{ fotos: FotoAmpliavel[]; indice: number } | null>(null);

  const porMaquina = useMemo(() => {
    const mapa = new Map<string, LinhaTabelaFinal[]>();
    for (const linha of tabela.linhas) {
      if (!mapa.has(linha.maquinaId)) mapa.set(linha.maquinaId, []);
      mapa.get(linha.maquinaId)!.push(linha);
    }
    return mapa;
  }, [tabela.linhas]);

  const exportar = async (formato: 'xlsx' | 'pdf') => {
    setExportando(formato);
    try {
      await onExportar(formato);
    } finally {
      setExportando(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          <Metrica rotulo="Máquinas" valor={String(tabela.totais.maquinas)} />
          <Metrica rotulo="Partes avaliadas" valor={String(tabela.totais.partes)} />
          <Metrica rotulo="Adequadas" valor={String(tabela.totais.adequadas)} tom="ok" />
          <Metrica rotulo="A corrigir" valor={String(tabela.totais.naoAdequadas)} tom="alerta" />
          {tabela.totais.pendentes > 0 && (
            <Metrica rotulo="Sem preço" valor={String(tabela.totais.pendentes)} tom="alerta" />
          )}
          <Metrica rotulo="Valor total" valor={moeda(tabela.totais.valorTotal)} tom="destaque" />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setMostrarFotos((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue"
          >
            {mostrarFotos ? <Rows3 className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
            {mostrarFotos ? 'Modo compacto' : 'Mostrar fotos'}
          </button>
          <button
            onClick={() => exportar('xlsx')}
            disabled={exportando !== null}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
          >
            {exportando === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel
          </button>
          <button
            onClick={() => exportar('pdf')}
            disabled={exportando !== null}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-schmersal-blue hover:bg-schmersal-blue-light disabled:opacity-50 text-white text-sm font-semibold transition-colors"
          >
            {exportando === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Proposta PDF
          </button>
        </div>
      </div>

      {tabela.totais.pendentes > 0 && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            {tabela.totais.pendentes} parte(s) ainda sem preço. Elas aparecem na tabela como pendentes
            e não entram no valor total.
          </span>
        </div>
      )}

      {tabela.porMaquina.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-schmersal-gray-darker rounded-xl">
          <p className="font-semibold text-schmersal-blue dark:text-white">Nenhuma máquina importada</p>
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1">
            Importe as Apreciações de Risco para montar a tabela.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {tabela.porMaquina.map((maquina) => {
            const linhas = porMaquina.get(maquina.id) ?? [];
            return (
              <div key={maquina.id} className="bg-white dark:bg-schmersal-gray-darker rounded-xl overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-schmersal-gray-light/60 dark:bg-schmersal-gray-dark/40 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                  <div className="flex items-center gap-3 min-w-0">
                    {mostrarFotos && maquina.fotos[0] && (
                      <button
                        onClick={() =>
                          setAmpliada({
                            fotos: maquina.fotos.map((f) => ({ url: f.url, urlHd: f.urlHd, legenda: f.legenda })),
                            indice: 0,
                          })
                        }
                        title="Ampliar fotos do equipamento"
                        className="group relative flex-shrink-0"
                      >
                        <img
                          src={toAbsoluteFotoUrl(maquina.fotos[0].url)}
                          alt=""
                          loading="lazy"
                          className="w-12 h-12 rounded object-cover"
                        />
                        <span className="absolute inset-0 rounded bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <ZoomIn className="h-4 w-4 text-white" />
                        </span>
                      </button>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-bold text-schmersal-blue dark:text-white truncate">{maquina.nome}</h3>
                      <p className="text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light truncate">
                        {[maquina.tag && `TAG ${maquina.tag}`, maquina.setor, maquina.numeroAr].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium">
                      {maquina.adequadas} adequada(s) · {maquina.naoAdequadas} a corrigir
                      {maquina.modoValor === 'unico' ? ' · valor fechado' : ''}
                    </div>
                    <div className="font-bold text-schmersal-blue dark:text-schmersal-turquoise text-lg">
                      {moeda(maquina.total)}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium">
                        {mostrarFotos && <th className="text-left font-bold px-4 py-2 w-20">Foto</th>}
                        <th className="text-left font-bold px-4 py-2 w-56">Parte</th>
                        <th className="text-left font-bold px-4 py-2">Solução proposta</th>
                        <th className="text-right font-bold px-4 py-2 w-32">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.length === 0 ? (
                        <tr>
                          <td colSpan={mostrarFotos ? 4 : 3} className="px-4 py-4 text-center text-schmersal-gray-medium text-xs">
                            Todas as partes desta máquina estão adequadas.
                          </td>
                        </tr>
                      ) : (
                        linhas.map((linha, i) => (
                          <tr
                            key={linha.parteId ?? `unico-${i}`}
                            className="border-t border-schmersal-gray-light dark:border-schmersal-gray-dark align-top"
                          >
                            {mostrarFotos && (
                              <td className="px-4 py-2.5">
                                {linha.fotoParte ? (
                                  <button
                                    onClick={() =>
                                      setAmpliada({
                                        fotos: [{
                                          url: linha.fotoParte!,
                                          urlHd: linha.fotoParteHd,
                                          legenda: `${linha.maquina} — ${linha.parte}`,
                                        }],
                                        indice: 0,
                                      })
                                    }
                                    title="Ampliar foto"
                                    className="group relative block"
                                  >
                                    <img
                                      src={toAbsoluteFotoUrl(linha.fotoParte)}
                                      alt=""
                                      loading="lazy"
                                      className="w-14 h-12 rounded object-cover"
                                    />
                                    <span className="absolute inset-0 rounded bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                      <ZoomIn className="h-3.5 w-3.5 text-white" />
                                    </span>
                                  </button>
                                ) : (
                                  <span className="w-14 h-12 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex items-center justify-center">
                                    <ImageOff className="h-3.5 w-3.5 text-schmersal-gray-medium" />
                                  </span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-2.5">
                              <div className="font-semibold text-schmersal-gray-dark dark:text-white">{linha.parte}</div>
                              {linha.localizacao && (
                                <div className="text-xs text-schmersal-gray-medium">{linha.localizacao}</div>
                              )}
                              {linha.hrn !== null && (
                                <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${corClassificacao(linha.classificacao)}`}>
                                  HRN {linha.hrn} · {linha.classificacao}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-schmersal-gray-dark dark:text-schmersal-gray-light">
                              <p className="whitespace-pre-line leading-snug">{linha.solucao ?? '—'}</p>
                              {linha.observacoes && (
                                <p className="text-xs italic text-schmersal-gray-medium mt-1">Obs.: {linha.observacoes}</p>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right whitespace-nowrap">
                              <CelulaValor linha={linha} />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ampliada && (
        <VisualizadorFoto
          fotos={ampliada.fotos}
          indiceInicial={ampliada.indice}
          onFechar={() => setAmpliada(null)}
        />
      )}
    </div>
  );
}

/**
 * "Sem custo" e "Não aplicável" valem zero na soma, mas mostrar "R$ 0,00" lê
 * como preço zerado por engano — o rótulo deixa a intenção explícita.
 */
function CelulaValor({ linha }: { linha: LinhaTabelaFinal }) {
  if (linha.status === 'sem_custo' || linha.status === 'nao_aplicavel') {
    return (
      <span className="text-xs font-semibold text-schmersal-gray-medium">
        {ROTULO_STATUS_PARTE[linha.status]}
      </span>
    );
  }
  if (linha.valor === null || linha.status === 'pendente') {
    return (
      <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
        {ROTULO_STATUS_PARTE[linha.status]}
      </span>
    );
  }
  return <span className="font-bold text-schmersal-blue dark:text-white">{moeda(linha.valor)}</span>;
}

function Metrica({ rotulo, valor, tom }: { rotulo: string; valor: string; tom?: 'ok' | 'alerta' | 'destaque' }) {
  const cor =
    tom === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
    : tom === 'alerta' ? 'text-amber-600 dark:text-amber-400'
    : tom === 'destaque' ? 'text-schmersal-blue dark:text-schmersal-turquoise'
    : 'text-schmersal-gray-dark dark:text-white';

  return (
    <div className="px-3 py-2 rounded-lg bg-white dark:bg-schmersal-gray-darker">
      <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium">{rotulo}</div>
      <div className={`font-bold ${cor} ${tom === 'destaque' ? 'text-lg' : ''}`}>{valor}</div>
    </div>
  );
}
